<template>
    <v-container fluid v-if="null != settlementDetails">
        <v-row justify="center">
            <!-- Summary -->
            <v-col cols="12">
                <v-card>
                    <div class="d-flex align-center">
                        <v-card-subtitle class="my-auto"
                            >Settlement details summary</v-card-subtitle
                        >
                        <v-spacer />
                        <date-picker-button
                            v-bind:date="settlementDetails.date"
                            v-on:date-change="$emit('date-change', $event)"
                        />
                    </div>
                    <v-divider />
                    <v-card-text>
                        <v-simple-table dense>
                            <tbody>
                                <tr>
                                    <th class="text--secondary">
                                        Amount with client:
                                    </th>
                                    <td>
                                        {{ amountWithClient | currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text--secondary">
                                        (including commission):
                                    </th>
                                    <td>
                                        {{ commissionWithClient | currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text--secondary">
                                        Delivery charge with restaurant:
                                    </th>
                                    <td>
                                        {{ chargeWithRestaurant | currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th class="text--secondary">
                                        Commission with restaurant:
                                    </th>
                                    <td>
                                        {{
                                            commissionWithRestaurant | currency
                                        }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Total owed to restaurant:</th>
                                    <td>
                                        <strong>{{
                                            (amountWithClient -
                                                commissionWithClient -
                                                chargeWithRestaurant -
                                                commissionWithRestaurant)
                                                | currency
                                        }}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Settlement status:</th>
                                    <td>
                                        <settlement-status-chip
                                            v-bind:settlement="
                                                settlementDetails.settlement
                                            "
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                    <v-divider />
                    <v-card-actions
                        v-if="
                            isOperator && null != settlementDetails.settlement
                        "
                    >
                        <v-spacer />
                        <v-btn
                            v-if="settlementDetails.settlement.is_settled"
                            class="mx-2"
                            tile
                            v-on:click="setSettled(false)"
                            v-bind:loading="$store.state.app.loading"
                            >Mark pending</v-btn
                        >
                        <v-btn
                            v-else
                            class="mx-2"
                            tile
                            color="success"
                            v-on:click="setSettled(true)"
                            v-bind:loading="$store.state.app.loading"
                        >
                            Mark settled
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <!-- End Summary -->
            <!-- Paid to client -->
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-subtitle>
                        Restaurant bill paid to client
                    </v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <v-card
                            class="mb-4"
                            v-for="order in settlementDetails.paid_client_orders"
                            v-bind:key="order.id"
                        >
                            <v-card-title>
                                {{ order.restaurant_amount | currency }}
                                <v-spacer />
                                <small class="text--secondary">{{
                                    new Date(order.completed_at) | date('p')
                                }}</small>
                            </v-card-title>
                            <v-card-subtitle class="py-0"
                                >Order ID: {{ order.id }}</v-card-subtitle
                            ><template v-if="order.destination != null">
                                <v-card-subtitle class="py-0"
                                    >Destination:
                                    {{ order.destination }}</v-card-subtitle
                                >
                            </template>
                            <v-card-subtitle class="pt-0"
                                >Commission:
                                {{
                                    order.commission_amount | currency
                                }}</v-card-subtitle
                            >
                        </v-card>
                        <v-card dark color="primary">
                            <v-card-subtitle class="pb-0"
                                >Total bill with client:</v-card-subtitle
                            >
                            <v-card-subtitle class="py-0"
                                >({{
                                    new Date(settlementDetails.date)
                                        | date('dd-MMM-yyyy')
                                }})</v-card-subtitle
                            >
                            <v-card-title>{{
                                amountWithClient | currency
                            }}</v-card-title>
                            <v-card-subtitle
                                >Including commission:
                                {{
                                    commissionWithClient | currency
                                }}</v-card-subtitle
                            >
                        </v-card>
                    </v-card-text>
                </v-card>
            </v-col>
            <!-- End Paid to client -->
            <!-- Paid to restaurant -->
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-subtitle>
                        Service charge and commission with restaurant
                    </v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <v-card
                            class="mb-4"
                            v-for="order in settlementDetails.paid_restaurant_orders"
                            v-bind:key="order.id"
                        >
                            <v-card-subtitle class="pb-0">
                                Order ID:
                                {{ order.id }}
                            </v-card-subtitle>
                            <template v-if="order.destination != null">
                                <v-card-subtitle class="py-0"
                                    >Destination:
                                    {{ order.destination }}</v-card-subtitle
                                >
                            </template>
                            <v-card-title>
                                {{ order.service_charge | currency }}
                                <v-spacer />
                                <small class="text--secondary">{{
                                    new Date(order.completed_at) | date('p')
                                }}</small>
                            </v-card-title>
                            <v-card-subtitle class="pb-0"
                                >(Service charge)</v-card-subtitle
                            >
                            <v-card-title>
                                {{ order.commission_amount | currency }}
                            </v-card-title>
                            <v-card-subtitle>
                                Commission from
                                {{ order.restaurant_amount | currency }}
                            </v-card-subtitle>
                        </v-card>
                        <v-card dark color="primary">
                            <v-card-subtitle class="pb-0">
                                Amount owed by restaurant:
                            </v-card-subtitle>
                            <v-card-subtitle class="py-0"
                                >({{
                                    new Date(settlementDetails.date)
                                        | date('dd-MMM-yyyy')
                                }})</v-card-subtitle
                            >
                            <v-card-title>{{
                                (chargeWithRestaurant +
                                    commissionWithRestaurant)
                                    | currency
                            }}</v-card-title>
                            <v-card-subtitle>
                                {{ chargeWithRestaurant | currency }} from
                                service charges<br />
                                {{ commissionWithRestaurant | currency }} from
                                commissions
                            </v-card-subtitle>
                        </v-card>
                    </v-card-text>
                </v-card>
            </v-col>
            <!-- End Paid to restaurant -->
        </v-row>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    components: {
        'date-picker-button': () => import('@/components/DatePickerButton.vue'),
        'settlement-status-chip': () =>
            import('@/components/SettlementStatusChip.vue')
    },
    props: {
        settlementDetails: {
            type: Object,
            required: true
        }
    },
    computed: {
        ...mapGetters({
            isOperator: 'auth/isOperator'
        }),
        amountWithClient() {
            // Total the restaurant amount from orders paid to client
            return this.settlementDetails.paid_client_orders.reduce(
                (accumulated, order) =>
                    accumulated + Number(order.restaurant_amount),
                0
            );
        },
        commissionWithClient() {
            // Total the commission from orders paid to client
            // This amount is already with the client
            return this.settlementDetails.paid_client_orders.reduce(
                (accumulated, order) =>
                    accumulated + Number(order.commission_amount),
                0
            );
        },
        chargeWithRestaurant() {
            // Total the delivery charge from orders paid to restaurant
            return this.settlementDetails.paid_restaurant_orders.reduce(
                (accumulated, order) =>
                    accumulated + Number(order.service_charge),
                0
            );
        },
        commissionWithRestaurant() {
            // Total the commission from orders paid to restaurant
            return this.settlementDetails.paid_restaurant_orders.reduce(
                (accumulated, order) =>
                    accumulated + Number(order.commission_amount),
                0
            );
        }
    },
    methods: {
        setSettled(isSettled) {
            this.$emit(
                'request-set-settled',
                this.settlementDetails.settlement,
                isSettled
            );
        }
    }
};
</script>
